import cn from "classnames";

import React, { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { ApiUserSearchHistory, getUserSearchHistory } from "@/api/user";
import { FlightWayEnum } from "@/constants";
import { AirportMapItem } from "@/constants/airport";
import usePlatform from "@/hooks/usePlatform";
import { SearchConditionType } from "@/hooks/useSearchCondition";
import { createSearchQuery } from "@/utils/searchQuery";

interface SearchHistoryModalProps {
  title?: string;
  buttonText?: string;
  className?: string;
}

// 将ApiUserSearchHistory.query转换为SearchConditionType
const convertHistoryToSearchCondition = (
  historyQuery: ApiUserSearchHistory["query"]
): SearchConditionType => {
  // 判断搜索类型，从search_type字段推断
  const isRoundTrip = historyQuery.search_type?.includes(FlightWayEnum.RoundTrip);

  const flightWay = isRoundTrip ? FlightWayEnum.RoundTrip : FlightWayEnum.OneWay;

  // 从date_range解析日期
  const fromDate = historyQuery.date_range?.from
    ? new Date(historyQuery.date_range.from).getTime()
    : 0;
  const toDate = historyQuery.date_range?.to ? new Date(historyQuery.date_range.to).getTime() : 0;

  return {
    flightWay,
    pax: historyQuery.pax || 1,
    children: historyQuery.children || 0,
    cabins: historyQuery.cabins?.join("&") || "Economy",
    range: flightWay === FlightWayEnum.OneWay ? true : false,
    rangeV2: false,
    fromLocation:
      historyQuery.departure_airports?.map(
        (iata) =>
          ({
            iata,
            name: "",
            city: "",
            subd: "",
            country: "",
          } as AirportMapItem)
      ) || [],
    toLocation:
      historyQuery.arrival_airports?.map(
        (iata) =>
          ({
            iata,
            name: "",
            city: "",
            subd: "",
            country: "",
          } as AirportMapItem)
      ) || [],
    oneWayDate: flightWay === FlightWayEnum.OneWay ? fromDate : 0,
    oneWayRangeStartDate: flightWay === FlightWayEnum.OneWay ? fromDate : 0,
    oneWayRangeEndDate: flightWay === FlightWayEnum.OneWay ? toDate : 0,
    roundTripDepartureDate: flightWay === FlightWayEnum.RoundTrip ? fromDate : 0,
    roundTripReturnDate: flightWay === FlightWayEnum.RoundTrip ? toDate : 0,
    departureRangeStartDate: 0,
    departureRangeEndDate: 0,
    returnRangeStartDate: 0,
    returnRangeEndDate: 0,
    programs: historyQuery.programs || [],
  };
};

// 表格内容组件
const SearchHistoryTable: React.FC<{
  list: ApiUserSearchHistory[];
  onRowClick: (query: ApiUserSearchHistory["query"]) => void;
}> = ({ list, onRowClick }) => {
  return (
    <TableContainer component={Paper}>
      <Table size="small" aria-label="supported codes table">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold", minWidth: 140, textAlign: "center" }}>
              Departure
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", minWidth: 140, textAlign: "center" }}>
              Arrival
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", minWidth: 200, textAlign: "center" }}>
              Date
            </TableCell>
            {/* <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>Cabin</TableCell> */}
            <TableCell sx={{ fontWeight: "bold", minWidth: 200, textAlign: "center" }}>
              Cabins
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Pax</TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Programs</TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Search Type</TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list
            .sort((a, b) => b.request_time - a.request_time)
            .map((row) => (
              <TableRow
                key={row.request_time}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
                onClick={() => onRowClick(row.query)}
              >
                <TableCell sx={{ textAlign: "center" }}>
                  {row.query.departure_airports.join(", ")}
                </TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  {row.query.arrival_airports.join(", ")}
                </TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  {row.query.date_range.from} - {row.query.date_range.to}{" "}
                  {row.query.return_date_range?.from && row.query.return_date_range?.to
                    ? `/ ${row.query.return_date_range.from} - ${row.query.return_date_range.to}`
                    : ""}
                </TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  {row.query.cabins.join(", ").replace(/Premium Economy/g, "Premium Econ")}
                </TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  {(row.query.pax + row.query.children).toString()}
                </TableCell>
                {/* <TableCell>{row.query.children.toString()}</TableCell> */}
                <TableCell sx={{ textAlign: "center" }}>
                  {row.query.programs.join(",") || "All"}
                </TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  {row.query.search_type
                    .replace("oneway", "One Way")
                    .replace("roundtrip", "Roundtrip")}
                </TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  <Button variant="text" size="small" color="primary">
                    Search
                  </Button>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// 主组件
const SearchHistoryModal: React.FC<SearchHistoryModalProps> = ({
  title = "Recent Searches",
  buttonText = "Recent",
  className,
}) => {
  const [open, setOpen] = useState(false);

  const { isMobile } = usePlatform();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleRowClick = (query: ApiUserSearchHistory["query"]) => {
    // 转换历史查询数据为搜索条件格式
    const searchCondition = convertHistoryToSearchCondition(query);

    // 创建URL查询参数
    const searchParams = createSearchQuery(searchCondition);

    // 构建新的URL参数字符串
    const urlParams = new URLSearchParams(searchParams as Record<string, string>);

    // 更新当前页面的URL，这会触发SearchBar组件重新读取参数
    window.open(`/flight?${urlParams.toString()}`, "_blank");

    // 关闭弹窗
    setOpen(false);
  };

  const [searchHistory, setSearchHistory] = useState<ApiUserSearchHistory[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setLoading(true);
    getUserSearchHistory()
      .then((res) => {
        setSearchHistory(res);
        console.log(res);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open]);

  return (
    <>
      <Button
        // variant="outlined"
        variant="text"
        // size="medium"
        color="primary"
        onClick={handleOpen}
        className={cn(className, "hover:bg-transparent !p-1 !leading-[12px] !text-[12px]")}
      >
        <HistoryIcon sx={{ fontSize: 16, color: "primary.main", mr: 0.3 }} />
        {buttonText}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: isMobile ? undefined : "1200px",
          },
        }}
        fullScreen={isMobile}
        aria-labelledby="supported-codes-dialog-title"
      >
        <DialogTitle id="supported-codes-dialog-title" sx={{ pb: 1 }}>
          <div className="flex items-center gap-2 text-primary">
            <HistoryIcon sx={{ fontSize: 20, color: "primary.main" }} />
            {title}
          </div>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <CircularProgress size={20} />
            </div>
          ) : searchHistory.length > 0 ? (
            <SearchHistoryTable list={searchHistory} onRowClick={handleRowClick} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Typography variant="body1">No recent searches</Typography>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SearchHistoryModal;
